import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, RefreshCw, ShoppingCart, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import ProductCard from './ProductCard';
import { useLanguage } from '../../contexts/LanguageContext';

// Map preference tags from backend to translation keys
const PREFERENCE_TAG_KEYS = {
  'organic preference': 'organic_preference',
  'vegan preference': 'vegan_preference',
  'frequently requested items': 'frequently_requested',
  'vegetarian preference': 'vegetarian_preference',
  'gluten-free preference': 'gluten_free_preference',
  'dairy-free preference': 'dairy_free_preference',
  'keto preference': 'keto_preference',
  'halal preference': 'halal_preference',
  'kosher preference': 'kosher_preference'
};

/**
 * PersonalizedRecommendations - Shows AI-recommended items based on user memory
 * 
 * Features:
 * - Memory-based personalized recommendations
 * - Shows recommendation reasons
 * - Fallback to popular items when no memory
 * - Cart integration with event tracking
 * - Multilingual support (uses global LanguageContext)
 */
export default function PersonalizedRecommendations({ onAddToCart }) {
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationType, setRecommendationType] = useState('');
  const [basedOn, setBasedOn] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [addedItems, setAddedItems] = useState(new Set());
  const [addingItem, setAddingItem] = useState(null);
  
  // Use global language context for consistent language across the app
  const { t, locale } = useLanguage();

  // Helper function to translate preference tags
  const translateTag = (tag) => {
    const lowerTag = tag.toLowerCase().trim();
    const key = PREFERENCE_TAG_KEYS[lowerTag];
    if (key) {
      return t(`consumer.recommendations.${key}`, tag);
    }
    // Try partial match
    for (const [pattern, transKey] of Object.entries(PREFERENCE_TAG_KEYS)) {
      if (lowerTag.includes(pattern.replace(' preference', '')) || pattern.includes(lowerTag.replace(' preference', ''))) {
        return t(`consumer.recommendations.${transKey}`, tag);
      }
    }
    return tag;
  };

  // Helper function to translate the backend message
  const translateMessage = (msg) => {
    if (!msg) return '';
    
    // Check if this is the "Recommended based on your preferences" pattern
    const prefixPattern = /^Recommended based on your preferences:\s*/i;
    if (prefixPattern.test(msg)) {
      const preferencesText = msg.replace(prefixPattern, '');
      const preferencesList = preferencesText.split(',').map(p => p.trim());
      const translatedPrefs = preferencesList.map(pref => translateTag(pref));
      return `${t('consumer.recommendations.based_on', 'Recommended based on your preferences:')} ${translatedPrefs.join(', ')}`;
    }
    
    return msg;
  };

  useEffect(() => {
    loadRecommendations();
  }, []);

  const loadRecommendations = async () => {
    setLoading(true);
    try {
      const response = await api.get('/nlu/recommendations?limit=8');
      setRecommendations(response.data.recommendations || []);
      setRecommendationType(response.data.recommendation_type);
      setBasedOn(response.data.based_on || []);
      setMessage(response.data.message);
    } catch (error) {
      console.error('Failed to load recommendations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (item, index) => {
    setAddingItem(item.id);
    
    try {
      await api.post('/events/recommendation-click', {
        item_id: item.id,
        item_name: item.name,
        recommendation_type: recommendationType,
        position: index + 1
      });
    } catch (error) {
      console.log('Event tracking failed:', error);
    }

    try {
      if (onAddToCart) {
        await onAddToCart(item);
      }
      setAddedItems(prev => new Set([...prev, item.id]));
      toast.success(`Added ${item.name} to cart`);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      toast.error('Failed to add to cart');
    } finally {
      setAddingItem(null);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card className="shadow-md">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <p className="text-gray-500">{t('consumer.recommendations.loading', 'Finding personalized recommendations...')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (recommendations.length === 0) {
    return (
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            {t('consumer.recommendations.title', 'Recommended For You')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingCart className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-600 mb-2">{t('consumer.recommendations.no_deals', 'No matching deals available right now')}</p>
            <p className="text-gray-400 text-sm">{t('consumer.recommendations.check_back', "Check back soon! We're always adding new deals.")}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={loadRecommendations}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              {t('consumer.recommendations.refresh', 'Refresh')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-md" data-testid="personalized-recommendations">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            {recommendationType === 'memory_based' 
              ? t('consumer.recommendations.title', 'Recommended For You')
              : t('consumer.recommendations.popular_deals', 'Popular Deals')
            }
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={loadRecommendations}
            className="text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        {message && (
          <p className="text-sm text-gray-600 mt-1">{translateMessage(message)}</p>
        )}
        {basedOn.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {basedOn.slice(0, 3).map((reason, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-white">
                {translateTag(reason)}
              </Badge>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {recommendations.slice(0, 8).map((item, index) => (
            <div key={item.id} className="relative group">
              <ProductCard 
                item={item}
                variant="compact"
                showStoreName={true}
                showPLU={false}
              />
              <div className="mt-2">
                <Button
                  size="sm"
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handleAddToCart(item, index)}
                  disabled={addingItem === item.id || addedItems.has(item.id)}
                  data-testid={`add-to-cart-${item.id}`}
                >
                  {addingItem === item.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : addedItems.has(item.id) ? (
                    '✓ Added'
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4 mr-1" />
                      {t('consumer.browse.add_to_cart', 'Add to Cart')}
                    </>
                  )}
                </Button>
              </div>
              {/* Match reason tooltip */}
              {item.match_reason && (
                <div className="text-xs text-emerald-600 mt-1 truncate" title={item.match_reason}>
                  {translateTag(item.match_reason)}
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
